// ProductSelector.jsx
// Reusable cascading Category → Brand → Product selector
// Mirrors the pattern from ReceiveInventory.jsx with inline creation support

import React, { Component } from 'react';
import { Form, Button } from 'react-bootstrap';
import InventoryAPI from '../../services/inventoryService';

class ProductSelector extends Component {
    state = {
        categories: [],
        products: [],
        selectedCategoryId: '',
        selectedBrand: '',
        selectedProductName: '',
        selectedProductId: '',

        // Inline creation
        showAddBrand: false,
        newBrand: '',
        showAddProduct: false,
        newProductName: '',

        loading: true
    };

    async componentDidMount() {
        try {
            const productsFetch = this.props.useMasterCatalog
                ? InventoryAPI.getMasterProducts()
                : InventoryAPI.getProducts(this.props.installerId);
            const [categories, products] = await Promise.all([
                InventoryAPI.getServiceCategories(),
                productsFetch
            ]);
            this.setState({
                categories: categories || [],
                products: products || [],
                loading: false
            });
        } catch (err) {
            console.error('Error loading product selector data:', err);
            this.setState({ loading: false });
        }
    }

    getUniqueBrands = () => {
        const { products, selectedCategoryId } = this.state;
        if (!selectedCategoryId) return [];
        return products
            .filter(p => String(p.category_id) === String(selectedCategoryId))
            .map(p => p.brand)
            .filter(brand => brand && brand.trim())
            .filter((brand, i, self) => self.findIndex(b => b.toLowerCase() === brand.toLowerCase()) === i)
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    };

    getFilteredProducts = () => {
        const { products, selectedCategoryId, selectedBrand } = this.state;
        if (!selectedCategoryId || !selectedBrand) return [];
        const brandLower = selectedBrand.toLowerCase();
        return products
            .filter(p =>
                String(p.category_id) === String(selectedCategoryId) &&
                (p.brand || '').toLowerCase() === brandLower &&
                (p.product_name || p.name || '').trim()
            )
            .sort((a, b) => (a.product_name || a.name || '').localeCompare(b.product_name || b.name || ''));
    };

    findProduct = () => {
        const { products, selectedProductId } = this.state;
        if (!selectedProductId) return null;
        return products.find(p => String(p.product_id || p.id) === String(selectedProductId)) || null;
    };

    handleCategoryChange = (e) => {
        this.setState({
            selectedCategoryId: e.target.value,
            selectedBrand: '',
            selectedProductName: '',
            selectedProductId: '',
            showAddBrand: false,
            newBrand: '',
            showAddProduct: false,
            newProductName: ''
        }, this.notifyChange);
    };

    handleBrandChange = (e) => {
        const value = e.target.value;
        if (value === 'add_new') {
            this.setState({
                showAddBrand: true,
                selectedBrand: '',
                selectedProductName: '',
                selectedProductId: '',
                showAddProduct: false,
                newProductName: ''
            }, this.notifyChange);
        } else {
            this.setState({
                selectedBrand: value,
                showAddBrand: false,
                newBrand: '',
                selectedProductName: '',
                selectedProductId: '',
                showAddProduct: false,
                newProductName: ''
            }, this.notifyChange);
        }
    };

    handleNewBrandChange = (e) => {
        this.setState({
            newBrand: e.target.value,
            selectedBrand: e.target.value
        }, this.notifyChange);
    };

    handleProductChange = (e) => {
        const value = e.target.value;
        if (value === 'add_new') {
            this.setState({
                showAddProduct: true,
                selectedProductName: '',
                selectedProductId: ''
            }, this.notifyChange);
        } else {
            const { products } = this.state;
            const product = products.find(p => String(p.product_id || p.id) === value);
            this.setState({
                selectedProductName: product ? (product.product_name || product.name) : '',
                selectedProductId: value,
                showAddProduct: false,
                newProductName: ''
            }, this.notifyChange);
        }
    };

    handleNewProductNameChange = (e) => {
        this.setState({
            newProductName: e.target.value,
            selectedProductName: e.target.value
        }, this.notifyChange);
    };

    notifyChange = () => {
        if (!this.props.onChange) return;
        const { selectedCategoryId, selectedBrand, selectedProductName, selectedProductId,
                showAddBrand, showAddProduct, categories } = this.state;

        const isNew = showAddBrand || showAddProduct;
        const product = this.findProduct();

        const selectedCategory = categories.find(c => String(c.category_id) === String(selectedCategoryId));

        this.props.onChange({
            product_id: selectedProductId ? parseInt(selectedProductId, 10) : null,
            product_name: selectedProductName,
            brand: selectedBrand,
            category_id: selectedCategoryId ? parseInt(selectedCategoryId, 10) : null,
            category_name: selectedCategory?.category_name || '',
            default_unit_id: selectedCategory?.default_unit_id || selectedCategory?.defaultUnitId || null,
            default_unit_code: selectedCategory?.default_unit_code || selectedCategory?.defaultUnitCode || null,
            isNew,
            // Pass existing product data for cost pre-fill etc.
            product: product || null
        });
    };

    // Create new product and return its ID
    createProductIfNeeded = async () => {
        const { selectedProductId, selectedProductName, selectedBrand,
                selectedCategoryId, showAddBrand, showAddProduct, categories, products } = this.state;

        // If existing product selected, just return its ID
        if (selectedProductId && !showAddBrand && !showAddProduct) {
            return parseInt(selectedProductId, 10);
        }

        // Validate
        if (!selectedProductName || !selectedProductName.trim()) {
            throw new Error('Product name is required');
        }
        if (!selectedBrand || !selectedBrand.trim()) {
            throw new Error('Brand is required');
        }
        if (!selectedCategoryId) {
            throw new Error('Category is required');
        }

        // Check for duplicates
        const newName = selectedProductName.trim().toLowerCase();
        const newBrand = selectedBrand.trim().toLowerCase();
        const duplicate = products.find(p => {
            const existingName = (p.product_name || p.name || '').toLowerCase();
            const existingBrand = (p.brand || '').toLowerCase();
            return existingName === newName && existingBrand === newBrand;
        });
        if (duplicate) {
            throw new Error(`Product "${selectedProductName.trim()}" by "${selectedBrand.trim()}" already exists.`);
        }

        // Get default unit from category
        const selectedCategory = categories.find(c => String(c.category_id) === String(selectedCategoryId));
        const unitId = selectedCategory ? (selectedCategory.default_unit_id || selectedCategory.defaultUnitId) : null;

        const result = await InventoryAPI.createProduct({
            name: selectedProductName.trim(),
            brand: selectedBrand.trim(),
            categoryId: parseInt(selectedCategoryId, 10),
            unitId,
            reorderPoint: 0,
            installerId: this.props.installerId
        });

        const newProductId = result.product_id || result.id || (result.data && (result.data.product_id || result.data.id));

        // Refresh products list
        const refreshed = await InventoryAPI.getProducts(this.props.installerId);
        this.setState({
            products: refreshed || [],
            selectedProductId: String(newProductId),
            showAddBrand: false,
            showAddProduct: false
        });

        return newProductId;
    };

    render() {
        const { categories, selectedCategoryId, selectedBrand, selectedProductId,
                showAddBrand, newBrand, showAddProduct, newProductName, loading } = this.state;

        if (loading) {
            return <div className="text-center py-2"><i className="fa-sharp fa-regular fa-spinner fa-spin"></i></div>;
        }

        const existingBrands = this.getUniqueBrands();
        const filteredProducts = this.getFilteredProducts();
        const hasBrandSelected = selectedBrand && selectedBrand.trim();

        return (
            <>
                {/* Category */}
                <Form.Group className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Form.Control as="select" value={selectedCategoryId} onChange={this.handleCategoryChange}>
                        <option value="">Select a category...</option>
                        {[...new Map(categories.map(c => [c.category_id, c])).values()]
                            .sort((a, b) => (a.category_name || '').localeCompare(b.category_name || ''))
                            .map(cat => (
                                <option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
                            ))}
                    </Form.Control>
                </Form.Group>

                {/* Brand */}
                {selectedCategoryId && (
                    <Form.Group className="mb-3">
                        <Form.Label>Brand</Form.Label>
                        {!showAddBrand ? (
                            <Form.Control as="select" value={selectedBrand} onChange={this.handleBrandChange}>
                                <option value="">
                                    {existingBrands.length === 0 ? 'No brands for this category - add new' : 'Select a brand...'}
                                </option>
                                {existingBrands.map(brand => (
                                    <option key={brand} value={brand}>{brand}</option>
                                ))}
                                <option value="add_new">+ Add New Brand</option>
                            </Form.Control>
                        ) : (
                            <div className="d-flex align-items-center">
                                <Form.Control type="text" value={newBrand}
                                    onChange={this.handleNewBrandChange}
                                    placeholder="Enter new brand name" autoFocus />
                                <Button variant="outline-secondary" size="sm" className="ml-2"
                                    onClick={() => this.setState({ showAddBrand: false, newBrand: '', selectedBrand: '' }, this.notifyChange)}>
                                    Cancel
                                </Button>
                            </div>
                        )}
                    </Form.Group>
                )}

                {/* Product Name */}
                {hasBrandSelected && (
                    <Form.Group className="mb-3">
                        <Form.Label>Product</Form.Label>
                        {!showAddProduct ? (
                            <Form.Control as="select" value={selectedProductId} onChange={this.handleProductChange}>
                                <option value="">
                                    {filteredProducts.length === 0 ? 'No products for this brand - add new' : 'Select a product...'}
                                </option>
                                {filteredProducts.map(p => (
                                    <option key={p.product_id || p.id} value={String(p.product_id || p.id)}>
                                        {p.product_name || p.name}
                                    </option>
                                ))}
                                <option value="add_new">+ Add New Product</option>
                            </Form.Control>
                        ) : (
                            <div className="d-flex align-items-center">
                                <Form.Control type="text" value={newProductName}
                                    onChange={this.handleNewProductNameChange}
                                    placeholder="Enter new product name" autoFocus />
                                <Button variant="outline-secondary" size="sm" className="ml-2"
                                    onClick={() => this.setState({ showAddProduct: false, newProductName: '', selectedProductName: '' }, this.notifyChange)}>
                                    Cancel
                                </Button>
                            </div>
                        )}
                    </Form.Group>
                )}
            </>
        );
    }
}

export default ProductSelector;
